//@ts-nocheck
import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { showToast } from "@banno/jha-wc/src/js/utils/toast-util";
import { ChangePasswordPopup } from "./ChangePasswordPopup";
import { fixedEncodeURIComponent, getRequest, postRequest } from "../../../utils/jha-fetch";
import { Apis } from "../../../utils/api-constants";
import { GlobalConstants, GlobalVariables } from "../../../utils/constants";
import { getRoleData } from "../../../utils/getRoleData";
import { getErrorMessage } from "../../../utils/getErrorMessage";
import {
  getCredentialHistoryPersistWarning,
  isCredentialUpdateSuccess,
} from "../../../utils/credential-history-warning";
import { routerConfig } from "../../../routers";
import { StatusConstants } from "../../../utils/apiStatusConstants";
import { AppRouters } from "../../../utils/app-router";
import { decryptValue, stringComparison } from "../../../utils/app-utils";
import { daysDiffernece } from "../../../utils/dateFormatter";
import { dateTimeFieldLabel } from "../../../utils/datetime-display";
import { formatLastSignOnForDisplay } from "../usersGridHelpers";
import "@banno/jha-wc/src/forms/jha-form-switch/jha-form-switch";
import DialogUtil from "@banno/jha-wc/src/js/utils/dialog-util";
import { openJhftDialog } from "../../../Components/JhftJhaDialog";
import {
  applyUserFormBaselineDefaults,
  captureUserFormBaseline,
  isUserFormDirty,
} from "../../../utils/user-form-dirty";
import formElementMixin from "@banno/jha-wc/src/js/mixins/form-element-mixin";
import Router from "@jack-henry/web-component-router";
import Cookies from "js-cookie";

@customElement("user-auth")
export class UserAuthentication extends formElementMixin(LitElement) {
  constructor() {
    super();
  }
  static get styles() {
    return [
      css`
        #formRow {
          display: flex;
          flex-direction: column;
          margin-bottom: 16px;
          max-width: 300px;
        }
        #formRowDisabled {
          display: flex;
          flex-direction: column;
          margin-bottom: 16px;
          pointer-events: none;
          opacity: 0.4;
        }
        @media (prefers-color-scheme: dark) {
          h2 {
            color: #a3d4ff;
          }
        }
        @media (prefers-color-scheme: light) {
          h2 {
            color: #06185f;
          }
        }
        h2 {
          margin-bottom: 18px;
        }
        jha-form-switch {
          width: 11rem;
        }
        #selectFieldDiv {
          display: flex;
          flex-direction: row;
          align-items: center;
        }
        #selectFieldDiv2 {
          display: flex;
          flex-direction: row;
          align-items: center;
          min-width: 200px;
        }
        jha-form-select {
          flex: 1;
          max-width: 280px;
          margin-left: 16px;
        }
        form {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        jha-button {
          max-width: 122px;
        }
        #actionBtns {
          display: flex;
          gap: 15px;
        }
      `,
    ];
  }

  @property({ type: Boolean }) hasUnsavedChanges = false;

  @property({ type: Boolean })
  isEditable = /readonly/i.test(decryptValue(Cookies.get("userRole")));

  @property({ type: String })
  userId = "";

  @property({ type: Boolean })
  isJHADomain = false

  @property({ type: Boolean })
  showCredentionals = false

  @property({ type: String })
  authType = "";

  @property({ type: Array }) userStatus = [];
  @property({ type: Array })
  policySelectOptions = [
    {
      name: "Default",
      value: 1,
    },
    {
      name: "Policy1",
      value: 2,
    },
  ];

  @property()
  userDetails = {};

  @property({ type: Boolean })
  sshCheck = false;

  @property()
  formData = {
    userState: "",
    authType: "",
    userType: "",
  };
  @property()
  lastSignedInDateTime = "";

  /** ACME-1050 Cancel/Save dirty-gate — see `src/utils/user-form-dirty.ts`. */
  _cancelBaseline: string | null = null;

  /**
   * Computed per render rather than latched, so no change handler can be missed.
   * Safe because every edit path reassigns a reactive property: the mixin's
   * `inputValueChange` replaces `formData`, and the switches set `sshCheck`.
   */
  private get isDirty(): boolean {
    return isUserFormDirty(this._cancelBaseline, this.formData);
  }

  onSshSwitchChange(e) {
    this.sshCheck = e.detail.checked;
    this.formData.authType = this.sshCheck ? "ssh" : "password";
    this.userDetails.authType = this.formData.authType;
  }

  onPassSwitchChange(e) {
    this.sshCheck = !e.detail.checked;
    this.formData.authType = this.sshCheck ? "ssh" : "password";
    this.userDetails.authType = this.formData.authType;
  }

  async onChangePasswordClick() {
    try {
      await DialogUtil.awaitDialog({
        dialog: new ChangePasswordPopup(this.userDetails?.userId, this.sshCheck, (data) =>
          this.submitCredentialChange(data),
        ),
      });
    } catch (e) { }
  }

  /**
   * Runs from the popup's Save while it is still open. Success toasts here; a failure
   * returns its message so the popup shows it next to the fields and stays open.
   */
  async submitCredentialChange(data): Promise<string | null> {
    // 401/404 come back as the raw Response (see postRequest). The body is a
    // MessageStatusModel: details sit on the body, or under messageStatus.
    const failure = async (response) => {
      let body = response;
      if (body && typeof body.json === "function") {
        try {
          body = await body.json();
        } catch {
          body = null;
        }
      }
      const details = Array.isArray(body?.details)
        ? body.details
        : Array.isArray(body?.messageStatus?.details)
          ? body.messageStatus.details
          : [];
      const message = getErrorMessage(details);
      return message.trim() ? message : "Something went wrong, please try again.";
    };
    const succeeded = (response, message: string) => {
      showToast({ message, role: "success", duration: GlobalVariables.TOAST_TIMEOUT });
      const historyWarning = getCredentialHistoryPersistWarning(response);
      if (historyWarning) {
        showToast({ message: historyWarning, role: "warning", duration: GlobalVariables.TOAST_TIMEOUT });
      }
      return null;
    };

    if (data?.checkPass) {
      const setPassword = await postRequest<{
        userId: string;
        newPassword: string;
      }>(Apis.UPDATE_PASSWORD, { userId: this.userDetails?.userId, newPassword: data?.newPassword });
      return isCredentialUpdateSuccess(setPassword)
        ? succeeded(setPassword, `Password For The User ${this.userDetails?.userId} Changed Successfully.`)
        : await failure(setPassword);
    }
    if (data?.checkSsh) {
      const setSsh = await postRequest<{
        userId: string;
        newSSHKey: string;
      }>(Apis.UPDATE_SSH, {
        userId: this.userDetails.userId,
        newSSHKey: JSON.stringify({ sshKey: data.newSSHKey }),
      });
      return isCredentialUpdateSuccess(setSsh)
        ? succeeded(setSsh, `SSH Key For The User ${this.userDetails.userId} Changed Successfully.`)
        : await failure(setSsh);
    }
    const reset = this.sshCheck
      ? await postRequest(Apis.RESET_SSH + `?userId=${this.userDetails.userId}`, undefined)
      : await postRequest(Apis.FORGOT_PASSWORD + `?userId=${this.userDetails.userId}`, undefined);
    if (reset?.messageStatus === null) {
      showToast({
        message: this.sshCheck ? "Reset SSH Key Email Sent Successfully." : "Reset Password Email Sent Successfully.",
        role: "success",
        duration: GlobalVariables.TOAST_TIMEOUT,
      });
      return null;
    }
    return await failure(reset);
  }

  onSaveClick() {
    this.dispatchEvent(
      new CustomEvent("onSaveAuthClick", {
        detail: this.formData,
        bubbles: true,
        composed: true,
      })
    );
    this.isEditable = false;
    // Do not re-baseline here. The parent PUT is async; if it fails the screen stays
    // open and isDirty must remain true so Save and Cancel still see the unsaved edit.
    // Success navigates away (EditUserTabView.onSaveAuthClick).
  }
  async connectedCallback() {
    super.connectedCallback();
    // Capture before the lookups below so a slow or failed load cannot leave the baseline
    // null, which reads as clean: Cancel would skip the prompt and Save would stay disabled.
    this._cancelBaseline = captureUserFormBaseline(this.formData);
    try {
      const statusList = await getRequest(Apis.USER_STATUS);
      this.userStatus = getRoleData(statusList?.list ?? []);
      this.formData = {
        userState: this.userDetails.status,
        authType: this.userDetails.authType,
        userType: this.userDetails.userType
      };
      this.lastSignedInDateTime = formatLastSignOnForDisplay(
        this.userDetails?.lastSignOnDateTime,
      ).display;
      if ((this.formData.userType === "businessadmin" || this.formData.userType === "enterpriseadmin")) {
        this.formData.authType = "password";
        this.userDetails.authType = this.formData.authType
      }

      // Every field above came from userDetails, not the user, so fold them all in. Doing it
      // here rather than after the GET_USER await below keeps a status change made while that
      // request is in flight visible to Cancel and Save.
      this._cancelBaseline = applyUserFormBaselineDefaults(this._cancelBaseline, this.formData);

      this.sshCheck = this.userDetails.authType === "ssh";

      if (this.userDetails?.email) {
        this.isJHADomain = GlobalConstants.JHADOMAIN.includes(this.userDetails?.email.split("@")[1]?.toLowerCase());
      }
      this.showCredentionals = !this.isJHADomain || !(this.formData.userType === "businessadmin" || this.formData.userType === "enterpriseadmin");

      const getUserData = await getRequest(Apis.GET_USER + `/${this.userDetails?.userId}`);
      this.authType = getUserData?.authType;
    } catch {
      showToast({
        message: "Unable to load user authentication details. Please refresh and try again.",
        role: "danger",
        duration: GlobalVariables.TOAST_TIMEOUT,
      });
    }
  }
  async onCancelClick() {
    const leave = () => {
      const router = new Router(routerConfig);
      router.go("/users");
    };

    // isEditable is really "is read-only" here (see its initializer). This guard was
    // commented out, so read-only users got an unsaved prompt they could not have caused.
    if (this.isEditable || !this.isDirty) {
      leave();
      return;
    }

    const result = await openJhftDialog({
      variant: "warning",
      title: "Unsaved Changes",
      message: "You have unsaved changes. Are you sure you want to leave without saving?",
      cancelLabel: "Stay",
      confirmLabel: "Leave Without Saving",
    });

    if (result === "confirm") {
      leave();
    }
  }

  disconnectedCallback() {
    this.dispatchEvent(
      new CustomEvent("onAuthTabChange", {
        detail: this.formData,
        bubbles: true,
        composed: true,
      })
    );
  }

  render() {
    const expiryDays = daysDiffernece(new Date(), new Date(this.userDetails.passwordExpiryDateTime));
    return html`
      <form>
        <div id="formRow">
          <h2>Account Status</h2>
          <div id="selectFieldDiv">
            <p>This account is:</p>
            <jha-tooltip bottom text="${stringComparison(decryptValue(Cookies.get("userId")), this.userDetails.userId) ? "You cannot inactivate yourself." : "Select active or inactive status of the user account."} ">
              <div id="selectFieldDiv2">
                <jha-form-select
                  data-auto-id="userStateSelect"
                  outline
                  .options=${this.userStatus}
                  ?disabled=${this.isEditable || stringComparison(decryptValue(Cookies.get("userId")), this.userDetails.userId)}
                  lableKey="name"
                  valueKey="value"
                  isValid
                  data-id="userState"
                  .value=${this.formData.userState}
                  @change=${this.inputValueChange}>
                </jha-form-select>
              </div>
            </jha-tooltip>
          </div>
          <p>${dateTimeFieldLabel("Last Sign-on")}: ${this.lastSignedInDateTime}</p>
        </div>
       ${(this.showCredentionals && !(this.formData.userType === "businessadmin" || this.formData.userType === "enterpriseadmin"))
        ? html`
        <div id="formRow">
          <h2>Credentials Required</h2>
          <p>Select credentials required for access:</p>
          <jha-form-switch
            .checked=${!this.sshCheck}
            @change=${this.onPassSwitchChange}
            ?disabled=${(this.formData.userType === "businessadmin" || this.formData.userType === "enterpriseadmin") || /readonly/i.test(decryptValue(Cookies.get("userRole")))}
            data-id="authType"
            ><span slot="left-label">Password</span></jha-form-switch
          >
          <jha-form-switch
            @change=${this.onSshSwitchChange}
            .checked=${this.sshCheck}
            ?disabled=${(this.formData.userType === "businessadmin" || this.formData.userType === "enterpriseadmin") || /readonly/i.test(decryptValue(Cookies.get("userRole")))}
            data-id="authType"
            ><span slot="left-label">SSH Public Key</span></jha-form-switch
          >
        </div>
        <div>
          <h2>${this.userDetails?.authType === "ssh" ? "SSH Public Key" : "Password"}</h2>
          <p>
            <strong style="color: ${expiryDays <= 0 ? "red" : "inherit"};">
              ${expiryDays <= 0 ? "Expired" : "Expires in: " + expiryDays + " days"}
            </strong>
          </p>
          <div id="selectFieldDiv">
            <p>Aging policy:</p>
            <jha-tooltip bottom text="Select the aging policy for the user account.">
              <div id="selectFieldDiv2">
                <jha-form-select
                  data-auto-id="agingPolicySelect"
                  outline
                  .options=${this.policySelectOptions}
                  lableKey="name"
                  valueKey="value"
                  isValid
                  disabled>
                </jha-form-select>
              </div>
            </jha-tooltip>
          </div>
        </div>
        <jha-tooltip bottom-left text=${this.sshCheck ? "Click to reset SSH Key" : "Click to reset password."}>
          <jha-button
            data-auto-id="changePasswordBtn"
            type="button"
            tertiary
            sync
            ?disabled=${stringComparison(this.userDetails.status, "INACTIVE") ||
          this.authType !== this.userDetails?.authType ||
          /readonly/i.test(decryptValue(Cookies.get("userRole")))}
            @click=${this.onChangePasswordClick}>
            ${this.sshCheck ? "Change SSH Key" : "Change Password"}
          </jha-button>
        </jha-tooltip>` : undefined}
        <div style="flex:1;"></div>
        <div id="actionBtns">
          ${!this.isEditable
        ? html` <jha-tooltip top-right text="Click to save changes.">
                <jha-button data-auto-id="saveBtn" ?disabled=${!this.isDirty} @click=${this.onSaveClick}>Save User</jha-button>
              </jha-tooltip>`
        : undefined}
          <jha-tooltip top-right text="Click to cancel changes.">
            <jha-button data-auto-id="cancelBtn" tertiary sync @click=${this.onCancelClick}>Cancel</jha-button>
          </jha-tooltip>
        </div>
      </form>
    `;
  }
}
declare global {
  interface HTMLElementTagNameMap {
    "user-auth": UserAuthentication;
  }
}
